import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import ProductCard from "@/components/ProductCard";
import RecommendationStrip from "@/components/RecommendationStrip";

const swatch = (c) => String(c || "").trim().toLowerCase().replace(/\s+/g, "");
import ZoomImage from "@/components/ZoomImage";
import WishlistButton from "@/components/WishlistButton";
import { Truck, Scissors, BadgeCheck } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [img, setImg] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [pin, setPin] = useState("");
  const [delivery, setDelivery] = useState(null);
  const cart = useCart();
  const { user } = useAuth();

  useEffect(() => {
    setImg(0);
    setQty(1);
    api.get(`/products/${id}`).then(({ data }) => {
      setData(data);
      setSize(data.product.sizes?.[0] || null);
      setColor(data.product.colors?.[0] || null);
    });
    // fire-and-forget behaviour track
    api.post("/track/view", { product_id: id }).catch(() => {});
  }, [id]);

  if (!data) return <div className="sq-container py-20">Loading…</div>;
  const p = data.product;
  const totalPrice = p.price * qty;

  const onAdd = async () => {
    if (!user) { toast("Sign in to add to cart"); navigate("/auth?redirect=" + encodeURIComponent(window.location.pathname)); return; }
    await cart.add(p.id, { quantity: qty, size, color });
    toast.success("Added to cart");
  };
  const onCheck = async () => {
    try { const { data } = await api.post("/delivery/check", { pincode: pin }); setDelivery(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invalid pincode"); }
  };

  return (
    <main className="sq-container pt-10" data-testid={`product-detail-${p.id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="relative aspect-[4/5] overflow-hidden bg-[#F5F5F5]">
            <ZoomImage src={p.images?.[img]} alt={p.name} className="w-full h-full" />
            {p.best_seller && (
              <span className="absolute top-3 left-3 bg-black text-white text-[10px] uppercase tracking-[0.2em] px-2 py-1 z-10">Best Seller</span>
            )}
            <WishlistButton id={p.id} className="absolute top-3 right-3 z-10" />
          </div>
          <div className="grid grid-cols-4 gap-3 mt-3">
            {p.images?.slice(0, 2).map((src, i) => (
              <button key={i} data-testid={`product-thumb-${i}`} onClick={() => setImg(i)} className={`aspect-square overflow-hidden border ${img === i ? "border-squire-jeans" : "border-transparent"}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="sq-eyebrow">{p.id} · {p.sub_category || p.category}</div>
          <h1 className="font-display text-3xl md:text-4xl mt-3 leading-tight">{p.name}</h1>
          <div className="font-display text-3xl mt-6">{formatINR(totalPrice)}</div>
          <p className="text-xs text-[#4B5563] mt-1">{formatINR(p.price)} × {qty}</p>
          <p className="text-[#4B5563] mt-4 max-w-lg">{p.description}</p>

          {p.colors?.length > 0 && (
            <div className="mt-6">
              <div className="sq-eyebrow">Colour{color ? `: ${color}` : ""}</div>
              <div className="flex flex-wrap gap-2 mt-2">
                {p.colors.map((c) => (
                  <button key={c} data-testid={`color-${c}`} onClick={() => setColor(c)} title={c}
                    className={`inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 text-xs uppercase tracking-[0.15em] border rounded-full ${color === c ? "border-squire-jeans ring-1 ring-squire-jeans" : "border-black/25 hover:border-black"}`}>
                    <span className="w-5 h-5 rounded-full border border-black/20" style={{ background: swatch(c) }} />
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
          {p.sizes?.length > 0 && (
            <div className="mt-6">
              <div className="sq-eyebrow">Size</div>
              <div className="flex flex-wrap gap-2 mt-2">
                {p.sizes.map((s) => (
                  <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)} className={`w-12 h-12 border ${size === s ? "border-squire-jeans bg-squire-jeans text-white" : "border-black/30"}`}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity — price increases automatically with quantity */}
          <div className="mt-6 flex items-center gap-3">
            <div className="sq-eyebrow">Qty</div>
            <div className="flex items-center border border-black/30">
              <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2">−</button>
              <span className="px-4" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-inc" onClick={() => setQty((q) => q + 1)} className="px-3 py-2">+</button>
            </div>
            <span className="text-sm text-[#4B5563]">= <strong className="text-black" data-testid="qty-total">{formatINR(totalPrice)}</strong></span>
          </div>

          <div className="mt-8 flex flex-wrap gap-4">
            <button data-testid="add-to-cart" onClick={onAdd} className="sq-btn-primary">Add to Cart</button>
            {p.customizable && (
              <Link to={`/bespoke`} data-testid="customize-cta" className="sq-btn-outline">
                <Scissors size={14} /> Customise
              </Link>
            )}
          </div>

          {/* Pincode delivery */}
          <div className="mt-10 border-t border-black/10 pt-6">
            <div className="sq-eyebrow">Delivery</div>
            <div className="flex items-center gap-2 mt-2">
              <Truck size={16} />
              <input data-testid="pincode-input" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Enter pincode" className="bg-transparent border-b border-black/30 outline-none py-2 flex-1" />
              <button data-testid="pincode-check" onClick={onCheck} className="sq-btn-outline !py-2 !px-4 !text-[10px]">Check</button>
            </div>
            {delivery && (
              <div className="mt-3 text-sm text-[#4B5563]" data-testid="delivery-result">
                <BadgeCheck className="inline mr-2 text-squire-jeans" size={14} />
                Delivers by <strong className="text-black">{delivery.eta_date}</strong> via {delivery.courier}. {delivery.cod_available ? "COD available." : ""}
              </div>
            )}
          </div>

          {/* Fabric swatch (optional) */}
          {p.fabric_image && (
            <div className="mt-8 flex items-center gap-4" data-testid="product-fabric-swatch">
              <img src={p.fabric_image} alt={p.fabric_name || "Fabric"} className="w-20 h-20 object-cover border border-black/10" />
              <div>
                <div className="sq-eyebrow text-[10px]">Fabric</div>
                <div className="text-sm text-black mt-1">{p.fabric_name || "—"}</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mt-10 text-xs text-[#4B5563]">
            {p.pattern_type && <Detail label="Type / Pattern" value={p.pattern_type} />}
            {p.fabric_name && !p.fabric_image && <Detail label="Fabric" value={p.fabric_name} />}
            {p.sleeve_type && <Detail label="Sleeves" value={p.sleeve_type} />}
            {p.composition && <Detail label="Composition" value={p.composition} />}
            {p.care && <Detail label="Care" value={p.care} />}
            {p.occasion && <Detail label="Occasion" value={p.occasion} />}
          </div>
        </div>
      </div>

      <RecommendationStrip context="product" productId={p.id} occasion={p.occasion} testid="product-recs" />

      {data.similar?.length > 0 && (
        <section className="mt-20">
          <div className="sq-eyebrow">Related Products</div>
          <h2 className="font-display text-3xl mt-2 mb-8">Similar pieces</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {data.similar.slice(0, 4).map((s) => <ProductCard key={s.id} product={s} />)}
          </div>
        </section>
      )}
    </main>
  );
}
function Detail({ label, value }) {
  return (
    <div>
      <div className="sq-eyebrow text-[10px]">{label}</div>
      <div className="text-black mt-1">{value}</div>
    </div>
  );
}
